// src/pages/SubmitCat.jsx
import { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { useNavigate } from "react-router-dom";

const ANTHROPIC_API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY;

export default function SubmitCat() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=form, 2=ai-review, 3=done
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [form, setForm] = useState({
    name: "", age: "", gender: "female", breed: "",
    description: "", ownerName: "", ownerPhone: "",
    vaccinated: false, neutered: false, microchipped: false, litterTrained: false,
  });
  const [aiReview, setAiReview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  function handlePhotoChange(e) {
    const files = Array.from(e.target.files).slice(0, 5);
    setPhotos(files);
    setPreviews(files.map(f => URL.createObjectURL(f)));
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.description || !form.ownerPhone) {
      setError("Please fill in name, description, and your WhatsApp number.");
      return;
    }
    setError("");
    setStep(2);
    setUploading(true);

    try {
      // AI reviews the submission
      const reviewRes = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: `You are an AI assistant that reviews cat adoption submissions and structures them for a database.
Given the owner's input, return ONLY a JSON object (no other text) with these fields:
{
  "shortDesc": "A compelling 1-sentence description for the browse card (max 100 chars)",
  "traits": ["up to 5 personality traits as short tags, e.g. Playful, Affectionate, Indoor"],
  "ageGroup": "kitten|adult|senior",
  "goodWith": ["children", "dogs", "cats"] (only include what applies based on description),
  "specialNeeds": "any special needs mentioned, or null",
  "approved": true,
  "notes": "any concerns or suggestions for the owner (optional)"
}`,
          messages: [{
            role: "user",
            content: `Cat name: ${form.name}
Age: ${form.age}
Breed: ${form.breed}
Gender: ${form.gender}
Description: ${form.description}
Vaccinated: ${form.vaccinated}
Neutered/Spayed: ${form.neutered}`
          }]
        })
      });
      const reviewData = await reviewRes.json();
      const reviewText = reviewData.content?.[0]?.text || "{}";
      const reviewJson = JSON.parse(reviewText.replace(/```json|```/g, "").trim());
      setAiReview(reviewJson);

      // Upload photos
      const photoUrls = [];
      for (const photo of photos) {
        const photoRef = ref(storage, `cats/${Date.now()}_${photo.name}`);
        await uploadBytes(photoRef, photo);
        const url = await getDownloadURL(photoRef);
        photoUrls.push(url);
      }

      // Save to Firestore
      await addDoc(collection(db, "cats"), {
        ...form,
        photos: photoUrls,
        shortDesc: reviewJson.shortDesc,
        traits: reviewJson.traits || [],
        ageGroup: reviewJson.ageGroup || "adult",
        goodWith: reviewJson.goodWith || [],
        specialNeeds: reviewJson.specialNeeds || null,
        status: "available",
        createdAt: serverTimestamp()
      });

      setUploading(false);
      setStep(3);
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
      setStep(1);
      setUploading(false);
    }
  }

  if (step === 2) return (
    <div style={styles.loadingPage}>
      <div style={styles.loadingCard}>
        <div className="spinner" style={{width:40,height:40,borderWidth:4}}/>
        <h2 style={styles.loadingTitle}>AI is reviewing {form.name}'s profile…</h2>
        <p style={styles.loadingText}>Uploading photos and structuring the listing. This takes a few seconds.</p>
        <div style={styles.steps}>
          {["Reviewing description","Generating tags","Uploading photos","Saving to database"].map((s,i)=>(
            <div key={i} style={styles.stepItem}>
              <span style={styles.stepDot}>·</span> {s}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (step === 3) return (
    <div style={styles.loadingPage}>
      <div style={styles.loadingCard}>
        <p style={{fontSize:56}}>🎉</p>
        <h2 style={styles.loadingTitle}>{form.name} is now listed!</h2>
        {aiReview && (
          <div style={styles.reviewResult}>
            <p style={styles.reviewLabel}>AI-generated tags:</p>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center",marginBottom:12}}>
              {(aiReview.traits||[]).map(t=><span key={t} className="tag">{t}</span>)}
            </div>
            <p style={{fontSize:14,color:"var(--muted)",fontStyle:"italic"}}>"{aiReview.shortDesc}"</p>
            {aiReview.notes && <p style={{marginTop:10,fontSize:13,color:"var(--mid)",background:"var(--warm)",padding:"10px 14px",borderRadius:10}}>{aiReview.notes}</p>}
          </div>
        )}
        <p style={styles.loadingText}>You'll receive WhatsApp notifications when someone wants to adopt.</p>
        <div style={{display:"flex",gap:12,marginTop:20}}>
          <button onClick={()=>navigate("/")} className="btn-primary">Browse All Cats</button>
          <button onClick={()=>{setStep(1);setForm({name:"",age:"",gender:"female",breed:"",description:"",ownerName:"",ownerPhone:"",vaccinated:false,neutered:false,microchipped:false,litterTrained:false});setPhotos([]);setPreviews([]);}} style={{padding:"12px 24px",borderRadius:50,border:"2px solid var(--rust)",background:"transparent",color:"var(--rust)",fontSize:15,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
            List Another Cat
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <p style={styles.eyebrow}>🐾 Rehome a cat</p>
          <h1 style={styles.title}>List Your Cat<br/><em style={{color:"var(--rust)",fontStyle:"italic"}}>for Adoption</em></h1>
          <p style={styles.subtitle}>Fill in the details below. Our AI will review and structure the listing automatically.</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Photos */}
          <div style={styles.section}>
            <label style={styles.sectionLabel}>📷 Photos (up to 5)</label>
            <div style={styles.photoUploadArea} onClick={()=>document.getElementById("photoInput").click()}>
              {previews.length === 0 ? (
                <>
                  <p style={{fontSize:36}}>🖼️</p>
                  <p style={{fontSize:15,color:"var(--muted)",marginTop:8}}>Click to upload photos</p>
                  <p style={{fontSize:12,color:"var(--muted)",marginTop:4}}>JPG, PNG up to 5MB each</p>
                </>
              ) : (
                <div style={styles.previewGrid}>
                  {previews.map((p,i)=>(
                    <div key={i} style={styles.previewImg}>
                      <img src={p} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    </div>
                  ))}
                  <div style={styles.addMore}>+ Add more</div>
                </div>
              )}
            </div>
            <input id="photoInput" type="file" accept="image/*" multiple style={{display:"none"}} onChange={handlePhotoChange}/>
          </div>

          {/* Basic Info */}
          <div style={styles.section}>
            <label style={styles.sectionLabel}>🐱 Cat Details</label>
            <div style={styles.grid2}>
              <Field label="Cat's Name *" name="name" value={form.name} onChange={handleChange} placeholder="e.g. Luna"/>
              <Field label="Age" name="age" value={form.age} onChange={handleChange} placeholder="e.g. 2 years"/>
              <div>
                <label style={styles.label}>Gender</label>
                <select name="gender" value={form.gender} onChange={handleChange} style={styles.input}>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
              </div>
              <Field label="Breed" name="breed" value={form.breed} onChange={handleChange} placeholder="e.g. Tabby, Persian…"/>
            </div>
          </div>

          {/* Description */}
          <div style={styles.section}>
            <label style={styles.sectionLabel}>📝 Description *</label>
            <textarea name="description" value={form.description} onChange={handleChange}
              placeholder="Tell us about the cat's personality, habits, what kind of home would suit them best…"
              style={{...styles.input, height:140, resize:"vertical"}}/>
            <p style={styles.hint}>Be descriptive! The AI will use this to generate tags and a summary card.</p>
          </div>

          {/* Health */}
          <div style={styles.section}>
            <label style={styles.sectionLabel}>💉 Health Status</label>
            <div style={styles.checkGrid}>
              {[["vaccinated","Vaccinated"],["neutered","Neutered / Spayed"],["microchipped","Microchipped"],["litterTrained","Litter Trained"]].map(([name,label])=>(
                <label key={name} style={styles.checkLabel}>
                  <input type="checkbox" name={name} checked={form[name]} onChange={handleChange} style={styles.checkbox}/>
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Owner Info */}
          <div style={styles.section}>
            <label style={styles.sectionLabel}>👤 Your Details</label>
            <div style={styles.grid2}>
              <Field label="Your Name" name="ownerName" value={form.ownerName} onChange={handleChange} placeholder="Your name"/>
              <Field label="WhatsApp Number *" name="ownerPhone" value={form.ownerPhone} onChange={handleChange} placeholder="+60 12-345 6789"/>
            </div>
            <p style={styles.hint}>Your WhatsApp number will be used to notify you when someone wants to adopt. It won't be shown publicly.</p>
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" className="btn-primary" style={{width:"100%",justifyContent:"center",padding:"16px",fontSize:17}}>
            🐾 Submit for AI Review & Publish
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, placeholder }) {
  return (
    <div>
      <label style={{ display:"block", fontSize:13, fontWeight:600, color:"var(--mid)", marginBottom:6 }}>{label}</label>
      <input name={name} value={value} onChange={onChange} placeholder={placeholder}
        style={{ width:"100%", border:"1.5px solid var(--peach)", borderRadius:10, padding:"10px 14px", fontSize:15, color:"var(--text)", background:"#fff", outline:"none", fontFamily:"'Outfit',sans-serif" }}
        onFocus={e=>e.target.style.borderColor="var(--rust)"}
        onBlur={e=>e.target.style.borderColor="var(--peach)"}/>
    </div>
  );
}

const styles = {
  page: { minHeight:"100vh", padding:"48px 0 80px" },
  container: { maxWidth:680, margin:"0 auto", padding:"0 24px" },

  header: { textAlign:"center", marginBottom:40 },
  eyebrow: { color:"var(--rust)", fontSize:13, fontWeight:600, letterSpacing:2, textTransform:"uppercase", marginBottom:12 },
  title: { fontFamily:"'Playfair Display',serif", fontSize:"clamp(32px,5vw,48px)", color:"var(--deep)", marginBottom:14 },
  subtitle: { color:"var(--muted)", fontSize:16, lineHeight:1.6 },

  form: { background:"#fff", borderRadius:24, padding:"36px 32px", boxShadow:"var(--shadow-m)", display:"flex", flexDirection:"column", gap:32 },

  section: {},
  sectionLabel: { display:"block", fontFamily:"'Playfair Display',serif", fontSize:18, color:"var(--deep)", marginBottom:16 },

  photoUploadArea: {
    border:"2px dashed var(--peach)", borderRadius:16,
    padding:"32px 20px", textAlign:"center", cursor:"pointer",
    background:"var(--cream)", transition:"border-color 0.2s",
    minHeight:120, display:"flex", alignItems:"center", justifyContent:"center",
  },
  previewGrid: { display:"flex", gap:10, flexWrap:"wrap", justifyContent:"center" },
  previewImg: { width:80, height:80, borderRadius:10, overflow:"hidden" },
  addMore: { width:80, height:80, borderRadius:10, border:"2px dashed var(--peach)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, color:"var(--muted)", cursor:"pointer" },

  grid2: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 },
  label: { display:"block", fontSize:13, fontWeight:600, color:"var(--mid)", marginBottom:6 },
  input: { width:"100%", border:"1.5px solid var(--peach)", borderRadius:10, padding:"10px 14px", fontSize:15, color:"var(--text)", background:"#fff", outline:"none", fontFamily:"'Outfit',sans-serif" },
  hint: { fontSize:12, color:"var(--muted)", marginTop:8 },

  checkGrid: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 },
  checkLabel: { display:"flex", alignItems:"center", gap:10, fontSize:15, color:"var(--text)", cursor:"pointer" },
  checkbox: { width:18, height:18, accentColor:"var(--rust)", cursor:"pointer" },

  error: { color:"var(--danger)", fontSize:14, textAlign:"center" },

  loadingPage: { minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24 },
  loadingCard: { background:"#fff", borderRadius:24, padding:"48px 40px", textAlign:"center", maxWidth:480, width:"100%", boxShadow:"var(--shadow-l)" },
  loadingTitle: { fontFamily:"'Playfair Display',serif", fontSize:26, color:"var(--deep)", marginTop:20, marginBottom:12 },
  loadingText: { color:"var(--muted)", fontSize:15, lineHeight:1.6 },
  steps: { marginTop:20, textAlign:"left", display:"inline-block" },
  stepItem: { fontSize:14, color:"var(--muted)", padding:"4px 0" },
  stepDot: { color:"var(--rust)", marginRight:6 },
  reviewResult: { background:"var(--warm)", borderRadius:14, padding:20, margin:"16px 0" },
  reviewLabel: { fontSize:13, color:"var(--muted)", marginBottom:10, fontWeight:600, textTransform:"uppercase", letterSpacing:1 },
};
